import { AppwriteException, ID, Permission, Role } from "appwrite";
import { z } from "zod";
import {
	getAppwriteClient,
	getCurrentUser,
} from "$/modules/appwrite/lib/appwrite";

const uploadFileSchema = z.object({
	asset: z.instanceof(File).optional(),
	bucketId: z.string(),
	publicRead: z.boolean().optional(),
});

export const uploadFile = async (
	data: z.infer<typeof uploadFileSchema>,
): Promise<string | null> => {
	if (!data.asset || data.asset.size === 0) return null;

	const appwrite = getAppwriteClient();
	const user = await getCurrentUser();

	const newFile = await appwrite.storage.createFile(
		data.bucketId,
		ID.unique(),
		data.asset,
		data.publicRead && user
			? [
					Permission.read(Role.any()),
					Permission.update(Role.user(user.$id)),
					Permission.delete(Role.user(user.$id)),
				]
			: undefined,
	);

	return newFile.$id;
};

const fileSchema = z.object({
	bucket: z.string(),
	assetId: z.string(),
});

export const getFile = async (data: z.infer<typeof fileSchema>) => {
	const appwrite = getAppwriteClient();
	const parsedData = await fileSchema.parseAsync(data);

	const storage = appwrite.storage;
	const url = new URL(
		storage.getFileView(parsedData.bucket, parsedData.assetId),
	);
	// Recheck permissions instead of reusing long-lived image responses without CORS headers.
	url.searchParams.set("_", Date.now().toString());
	// Use the SDK's session headers when third-party cookies are unavailable.
	const download: Promise<ArrayBuffer> = storage.client.call(
		"get",
		url,
		{},
		{},
		"arrayBuffer",
	);
	const [bytes, meta] = await Promise.all([
		download,
		storage.getFile(parsedData.bucket, parsedData.assetId),
	]);

	return {
		buffer: new Blob([bytes], { type: meta.mimeType }),
		meta,
	};
};

const replaceFileSchema = fileSchema.extend({
	newAsset: z.instanceof(File),
	publicRead: z.boolean().optional(),
});

export const replaceFile = async (data: z.infer<typeof replaceFileSchema>) => {
	const appwrite = getAppwriteClient();
	const user = await getCurrentUser();

	try {
		await appwrite.storage.deleteFile(data.bucket, data.assetId);
		const file = await appwrite.storage.createFile(
			data.bucket,
			ID.unique(),
			data.newAsset,
			data.publicRead && user
				? [
						Permission.read(Role.any()),
						Permission.update(Role.user(user.$id)),
						Permission.delete(Role.user(user.$id)),
					]
				: undefined,
		);

		return file.$id;
	} catch (error) {
		if (error instanceof AppwriteException) {
			console.error("Can't replace Asset", error.message);
		}
	}
};

export const updateFile = async (data: z.infer<typeof fileSchema>) => {
	const appwrite = getAppwriteClient();
	const parsedData = fileSchema.parse(data);
	await getCurrentUser();
	await appwrite.storage.updateFile(parsedData.bucket, parsedData.assetId);
};

export const deleteFile = async (data: z.infer<typeof fileSchema>) => {
	const appwrite = getAppwriteClient();
	const parsedData = await fileSchema.parseAsync(data);
	await getCurrentUser();
	await appwrite.storage.deleteFile(parsedData.bucket, parsedData.assetId);
};
